import { Reservation } from "@workspace/domain/src/features/reservation";
import dayjs from "@workspace/domain/src/utils/dayjs";
import * as z from "zod";

// Date range filter schema
const DateRangeSchema = z.object({
  startDate: z
    .union([
      z.iso.datetime().transform((date) => dayjs(date).toDate()),
      z.date(),
    ])
    .optional()
    .default(dayjs().subtract(30, "day").startOf("day").toDate()),

  endDate: z
    .union([
      z.iso.datetime().transform((date) => dayjs(date).toDate()),
      z.date(),
    ])
    .optional()
    .default(dayjs().endOf("day").toDate()),
});

// Time series data point for charts
const TimeSeriesDataPointSchema = z.object({
  period: z.string(),
  count: z.number(),
  amount: z.number().optional(),
});

// Breakdown data point for pie charts
const BreakdownDataPointSchema = z.object({
  name: z.string(),
  value: z.number(),
  count: z.number(),
});

// Payment entry for ledger
const PaymentEntrySchema = z.object({
  id: z.string(),
  transactionId: z.string().nullable(),
  status: z.enum(Reservation.PaymentStatus),
  amount: z.number(),
  paymentMethod: z.string().nullable(),
  sourcePan: z.string().nullable(),
  createdAt: z.date(),
  reservationId: z.string(),
  reservationSerial: z.string(),
  clientId: z.string(),
  clientName: z.string().nullable(),
  tripId: z.string(),
  tripSerial: z.string(),
  tripName: z.string(),
});

// Payment filters
const PaymentsFiltersSchema = z.object({
  startDate: z
    .union([
      z.iso.datetime().transform((date) => dayjs(date).toDate()),
      z.date(),
    ])
    .optional(),
  endDate: z
    .union([
      z.iso.datetime().transform((date) => dayjs(date).toDate()),
      z.date(),
    ])
    .optional(),
  status: z.enum(Reservation.PaymentStatus).optional(),
  tripId: z.string().optional(),
  search: z.string().optional(),
  page: z.number().int().min(0).default(0),
  limit: z.number().int().min(1).max(1000).default(20),
  sortBy: z.enum(["createdAt", "amount", "status"]),
  sortOrder: z.enum(["asc", "desc"]),
});

export const AdminFinance = {
  DateRange: DateRangeSchema,
  TimeSeriesDataPoint: TimeSeriesDataPointSchema,
  BreakdownDataPoint: BreakdownDataPointSchema,
  PaymentEntry: PaymentEntrySchema,
  PaymentsFilters: PaymentsFiltersSchema,
};

export type AdminFinance = {
  DateRange: z.infer<typeof DateRangeSchema>;
  TimeSeriesDataPoint: z.infer<typeof TimeSeriesDataPointSchema>;
  BreakdownDataPoint: z.infer<typeof BreakdownDataPointSchema>;
  PaymentEntry: z.infer<typeof PaymentEntrySchema>;
  PaymentsFilters: z.infer<typeof PaymentsFiltersSchema>;
};
